/**
 * Rekognition Analytics Page
 *
 * AWS Rekognition face recognition analytics including
 * API calls, match rates, coverage, processing times, and costs.
 */

import { useState, useEffect } from 'react';
import { thirdPartyAnalyticsAPI } from '../services/api';
import { useEnvironment } from '../context/EnvironmentContext';
import PageHeader from '../components/analytics/PageHeader';
import MetricCard from '../components/analytics/MetricCard';
import TrendChart from '../components/founder-dashboard/TrendChart';
import DataTable from '../components/founder-dashboard/DataTable';
import DistributionChart from '../components/founder-dashboard/DistributionChart';
import {
  CameraIcon,
  CheckCircleIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  ClockIcon,
  UserGroupIcon,
  FaceSmileIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline';

export default function RekognitionPage() {
  const { currentEnv } = useEnvironment();
  const [overview, setOverview] = useState(null);
  const [byOperation, setByOperation] = useState(null);
  const [byOrganization, setByOrganization] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [processingTimes, setProcessingTimes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [overviewRes, operationsRes, orgsRes, coverageRes, timesRes] = await Promise.all([
        thirdPartyAnalyticsAPI.getRekognitionOverview(),
        thirdPartyAnalyticsAPI.getRekognitionByOperation(),
        thirdPartyAnalyticsAPI.getRekognitionByOrganization(),
        thirdPartyAnalyticsAPI.getRekognitionCoverage(),
        thirdPartyAnalyticsAPI.getRekognitionProcessingTimes(),
      ]);
      setOverview(overviewRes.data);
      setByOperation(operationsRes.data);
      setByOrganization(orgsRes.data);
      setCoverage(coverageRes.data);
      setProcessingTimes(timesRes.data);
    } catch (err) {
      console.error('[RekognitionPage] Error fetching data:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentEnv]);

  // Get cost status
  const getCostStatus = () => {
    const cost = overview?.estimatedMonthlyCost || 0;
    if (cost > 50) return 'critical';
    if (cost > 25) return 'warning';
    return 'healthy';
  };

  if (error) {
    return (
      <div className="p-6">
        <PageHeader title="Rekognition Analytics" />
        <div className="mt-6 bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <p className="text-red-800 font-medium">Error loading Rekognition analytics</p>
          <p className="text-red-600 text-sm mt-1">{error}</p>
          <button
            onClick={fetchData}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Operation distribution - backend returns array directly, use apiCalls for count
  const operationDistribution = {};
  (byOperation?.operations || []).forEach((op) => {
    operationDistribution[op.operation] = op.apiCalls;
  });

  // Organization table columns - backend returns camelCase
  const orgColumns = [
    { key: 'organizationName', header: 'Organization', sortable: true },
    { key: 'facesIndexed', header: 'Indexed', sortable: true },
    { key: 'apiCalls', header: 'Sessions', sortable: true },
    {
      key: 'matchRate',
      header: 'Coverage',
      sortable: true,
      render: (val, row) => {
        const total = (row.facesMatched || 0) + (row.facesUnknown || 0);
        const rate = total > 0 ? ((row.facesMatched || 0) / total) * 100 : 0;
        return `${rate.toFixed(1)}%`;
      },
    },
    {
      key: 'estimatedCost',
      header: 'Cost',
      sortable: true,
      render: (val) => `$${(val || 0).toFixed(2)}`,
    },
  ];

  // Coverage trend data - backend returns 'trend' array with 'coveragePct' field
  const coverageTrendData = (coverage?.trend || []).map(t => ({
    date: t.date,
    coverage: t.coveragePct,
  }));
  const coverageTrendLines = [
    { key: 'coverage', name: 'Coverage %', color: '#22C55E' },
  ];

  return (
    <div className="p-6 space-y-8">
      {/* Header */}
      <PageHeader
        title="Rekognition Analytics"
        description="Face recognition performance and usage metrics"
        onRefresh={fetchData}
        loading={loading}
      />

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="API Calls (30d)"
          value={overview?.totalApiCalls30d}
          icon={CameraIcon}
          loading={loading}
        />
        <MetricCard
          title="Match Rate"
          value={overview?.matchRate}
          format="percent"
          icon={CheckCircleIcon}
          description="% of faces detected that matched an indexed face in the system"
          loading={loading}
        />
        <MetricCard
          title="Coverage"
          value={coverage?.overall?.overallCoveragePct}
          format="percent"
          icon={ChartBarIcon}
          description="% of attendees marked present via photo recognition vs total attendees"
          loading={loading}
        />
        <MetricCard
          title="Est. Monthly Cost"
          value={overview?.estimatedMonthlyCost}
          format="currency"
          icon={CurrencyDollarIcon}
          status={getCostStatus()}
          loading={loading}
        />
      </div>

      {/* By Operation + Processing Times */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* By Operation */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Calls by Operation
          </h3>
          {loading ? (
            <div className="h-48 bg-gray-50 rounded-lg animate-pulse"></div>
          ) : (
            <DistributionChart
              data={operationDistribution}
              height={200}
              showPercentage
            />
          )}
        </div>

        {/* Processing Times - use overview data for simple display */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Processing Times
          </h3>
          {loading ? (
            <div className="h-48 bg-gray-50 rounded-lg animate-pulse"></div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <ClockIcon className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-500">Average</span>
                </div>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {overview?.avgProcessingMs || 0}ms
                </p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <ClockIcon className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-500">P50</span>
                </div>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {overview?.avgProcessingMs || 0}ms
                </p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <ClockIcon className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-500">P95</span>
                </div>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {overview?.p95ProcessingMs || 0}ms
                </p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <ClockIcon className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-500">P99</span>
                </div>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {overview?.p95ProcessingMs || 0}ms
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Face Recognition Stats */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Face Recognition Stats
        </h3>
        {loading ? (
          <div className="h-24 bg-gray-50 rounded-lg animate-pulse"></div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
              <div className="flex items-center gap-2">
                <FaceSmileIcon className="w-5 h-5 text-blue-600" />
                <span className="text-sm text-blue-700">Faces Indexed</span>
              </div>
              <p className="text-2xl font-bold text-blue-800 mt-1">
                {overview?.facesIndexed || 0}
              </p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg border border-green-100">
              <div className="flex items-center gap-2">
                <CheckCircleIcon className="w-5 h-5 text-green-600" />
                <span className="text-sm text-green-700">Faces Matched</span>
              </div>
              <p className="text-2xl font-bold text-green-800 mt-1">
                {overview?.facesMatched || 0}
              </p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2">
                <QuestionMarkCircleIcon className="w-5 h-5 text-gray-600" />
                <span className="text-sm text-gray-600">Faces Unknown</span>
              </div>
              <p className="text-2xl font-bold text-gray-800 mt-1">
                {overview?.facesUnknown || 0}
              </p>
            </div>
            <div className="p-4 bg-purple-50 rounded-lg border border-purple-100">
              <div className="flex items-center gap-2">
                <ChartBarIcon className="w-5 h-5 text-purple-600" />
                <span className="text-sm text-purple-700">Match Rate</span>
              </div>
              <p className="text-2xl font-bold text-purple-800 mt-1">
                {(overview?.matchRate || 0).toFixed(1)}%
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Coverage by Organization */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Coverage by Organization (Top 20)
        </h3>
        <DataTable
          data={byOrganization?.organizations || []}
          columns={orgColumns}
          loading={loading}
          emptyMessage="No organization data available"
        />
      </div>

      {/* Coverage Trend */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Coverage Trend
        </h3>
        {loading ? (
          <div className="h-64 bg-gray-50 rounded-lg animate-pulse"></div>
        ) : (
          <TrendChart
            data={coverageTrendData}
            lines={coverageTrendLines}
            height={250}
            xAxisKey="date"
            type="area"
          />
        )}
      </div>
    </div>
  );
}
